/**
 * Card overview image dimensions and positioning
 */
export const CARD_OVERVIEW_CONSTANTS = {
  imageDimensionVertical: {
    desktopWidth: 190,
    mobileWidth: 190,
    tabletWidth: 202,
  },
  oneColumn: {
    desktopTop: -30,
    tabletTop: -22,
    mobileTop: -19,
  },
  twoColumn: {
    mobileTop: -26,
  },
  threeColumn: {
    tabletTop: -38,
  },
  stickyCardImageDimension: {
    desktopWidth: 120,
    mobileWidth: 48,
    tabletWidth: 80,
  },
  stickyCardImageVertical: {
    desktopWidth: 256,
    mobileWidth: 216,
    tabletWidth: 168,
  },
} as const;

/**
 * Bank landing page tab configuration
 */
export const BANK_LANDING_TAB_LIST = [
  {
    tabTitle: 'Personal',
    tabValue: 'personal',
    isActive: true,
  },
  {
    tabTitle: 'Business',
    tabValue: 'business',
  },
] as const;

/**
 * Selector list for column container alignment
 */
export const SELECTOR_LIST = [
  {
    selector: 'overview',
    rowCount: 1,
  },
  {
    selector: 'detail',
    rowCount: 1,
  },
  {
    selector: 'list-detail',
    rowCount: 1,
  },
  {
    selector: 'spend',
    rowCount: 3,
  },
  {
    selector: 'points-detail',
    rowCount: 1,
  },
] as const;

/**
 * Character count limits for points earned section
 */
export const POINTS_EARNED_CHARACTER_COUNT = {
  TWO_COLUMN: 233,
  THREE_COLUMN: 160,
} as const;

/**
 * Card-specific codes
 */
export const CARD_CODES = {
  CHASE_BOUNDLESS: 'CH-PREP',
} as const;

/**
 * ECM (Enhanced Card Management) cobrand issuer/product identifiers.
 * Used to select the member's Chase card from the wallet payment methods.
 * CHASE_ISSUER_CODE matches cobrand.issuer.code (CH = Chase) and
 * CHASE_PRODUCT_CODES match cobrand.issuerProduct.code (PREP = Boundless, BOLD = Bold).
 */
export const CHASE_ISSUER_CODE = 'CH';
export const CHASE_PRODUCT_CODES = ['PREP', 'BOLD'];

/**
 * Default ECM card priority (wallet product codes) used when no Harness priority
 * order is provided. Defaults to Boundless (PREP).
 */
export const CHASE_DEFAULT_PRIORITY = ['PREP'];

/**
 * Card type enumeration
 */
export enum COBRAND_CARD_TYPE {
  BUSINESS = 'business',
  PERSONAL = 'personal',
  NONE = 'none',
}

/**
 * Type alias for cobrand card types
 */
export type CardType = `${COBRAND_CARD_TYPE}`;

/**
 * Column layout types (used across card components)
 */
export type ColumnLayout = '1-column' | '2-column' | '3-column';

/**
 * Card layout types
 */
export enum CARD_LAYOUT_TYPE {
  VERTICAL = 'vertical-card-layout',
  HORIZONTAL = 'horizontal-card-layout',
  DEFAULT = 'default',
}

/**
 * Layout orientation constant
 */
export const VERTICAL = 'vertical' as const;

/**
 * Link types for buttons and anchors
 */
export enum LinkType {
  EXTERNAL = 'external',
  INTERNAL = 'internal',
}

/**
 * Link target types
 */
export enum LinkTarget {
  BLANK = '_blank',
  SELF = '_self',
}

/**
 * Card variant orientations
 */
export enum CardVariant {
  VERTICAL = 'vertical',
  HORIZONTAL = 'horizontal',
}

/**
 * String boolean values used in AEM properties
 */
export enum StringBoolean {
  TRUE = 'true',
  FALSE = 'false',
}

/**
 * ARIA labels for accessibility
 */
export const ARIA_LABELS = {
  OPEN_NEW_TAB: 'Open in new tab',
  COMPARE: 'Compare',
} as const;

/**
 * Button style classes for CTA buttons
 */
export enum ButtonStyleClass {
  PRIMARY = 'm-button-primary',
  PRIMARY_EXTERNAL = 'm-button-primary m-button-external',
  SECONDARY = 'm-button-secondary',
  SECONDARY_EXTERNAL = 'm-button-secondary m-button-external',
  TERTIARY = 'm-link-tertiary-button',
  TERTIARY_EXTERNAL = 'm-link-tertiary-button-external',
}

/**
 * CTA button types from AEM
 */
export enum CTAButtonType {
  PRIMARY = 'primaryButton',
  SECONDARY = 'secondaryButton',
  TERTIARY = 'tertiaryButton',
}

/**
 * Session and endpoint constants
 */
export const SESSION_CONSTANTS = {
  COBRAND_DATA: 'cobrandData',
  CREDIT_CARD_ENDPOINT: 'creditcardendpoint',
} as const;

/**
 * Comparison tray slot indices
 */
export const COMPARISON_TRAY_SLOTS = {
  DESKTOP: [0, 1, 2],
  MOBILE: [0, 1],
} as const;

/**
 * Card comparison limits
 */
export const CARD_COMPARISON_LIMITS = {
  MAX_CARDS_DESKTOP: 3,
  MAX_CARDS_MOBILE: 2,
} as const;

/**
 * CSS class replacements for card title variants
 */
export const CSS_CLASS_REPLACEMENTS = {
  CARD_TITLE_VERTICAL_TO_HORIZONTAL: {
    FROM: 't-subtitle-m',
    TO: 't-label-alt-xs',
  },
} as const;

export enum RESOLVED_URL {
  CREDITCARDURL = 'credit-cards',
}

export const GLOBAL_NAV_HEIGHT = {
  mobile: 52,
  desktop: 88,
};
